import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();
console.log("Backend URL:", process.env.REACT_APP_BACKEND_URL);

const GoogleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 533.5 544.3"
    xmlns="http://www.w3.org/2000/svg"
    className="mr-2"
  >
    <path
      d="M533.5 278.4c0-18.7-1.5-37-4.5-54.7H272v103.7h146.9c-6.3 34.2-25.2 63.3-53.6 82.8v68h86.6c50.8-46.8 80.6-115.8 80.6-199.8z"
      fill="#4285F4"
    />
    <path
      d="M272 544.3c72.6 0 133.6-24 178.2-65.3l-86.6-68c-24.1 16.2-55 25.7-91.6 25.7-70.4 0-130-47.6-151.4-111.3H30.4v69.9c44.5 87.9 136.1 149 241.6 149z"
      fill="#34A853"
    />
    <path
      d="M120.6 320.1c-10.3-30.3-10.3-62.9 0-93.2V156.9H30.4c-39.7 78.8-39.7 172.1 0 250.9l90.2-68.7z"
      fill="#FBBC05"
    />
    <path
      d="M272 107.7c39.5 0 75 13.6 102.9 40.4l77.2-77.2C405.3 24.6 348.2 0 272 0 166.5 0 74.9 61.1 30.4 149l90.2 69.9c21.4-63.6 81-111.2 151.4-111.2z"
      fill="#EA4335"
    />
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    console.log("LOGIN CLICKED"); // 
    e.preventDefault();
    setError("");
    setForgotPasswordMessage("");

    if (!BACKEND_URL) {
      setError("Internal error: Backend URL is not configured.");
      return;
    }
console.log("Submitting login with:", {
  email: email.trim(),
  password: password ? "****" : "EMPTY",
  BACKEND_URL,
});
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
console.log("Login API status:", response.status);

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data?.detail || `Login failed with status ${response.status}`);
        return;
      }

      if (!data.token) {
        setError("No token received from server.");
        return;
      }

      console.log("Login Response:", data);

      // Save JWT token
      localStorage.setItem("jwt_token", data.token);
      localStorage.setItem("user_id", data.user_id);
      localStorage.setItem("passwordChangeRequired", data.flag === 1 ? "true" : "false");

      // Verify token
      const verifyResp = await fetch(`${BACKEND_URL}/api/auth/verify-token`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${data.token}`,
        },
      });

      if (!verifyResp.ok) {
        const verifyData = await verifyResp.json().catch(() => ({}));
        setError(verifyData?.detail || `Token verification failed (${verifyResp.status})`);
        return;
      }

      if (Number(data.flag) === 1) {
        console.log("Redirecting to /change-password...");
        navigate("/change-password");
      } else {
        console.log("Redirecting to /dashboard...");
        navigate("/dashboard");
      }
    } catch (err) {
      setError(
        err?.message?.includes("fetch") || err instanceof TypeError
          ? "Network or CORS error. Check backend URL or server status."
          : err.message || "Unexpected error occurred."
      );
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setForgotPasswordMessage("");

    if (!email.trim()) {
      setForgotPasswordMessage("Please enter your email address first.");
      return;
    }

    if (!BACKEND_URL) {
      setError("Internal error: Backend URL is not configured.");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/email/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data?.detail || `Failed to send reset email (status ${response.status}).`);
        return;
      }

      setForgotPasswordMessage(
        "We've sent your updated login credentials. Please check your inbox and spam folder."
      );
    } catch (err) {
      setError(
        err?.message?.includes("fetch") || err instanceof TypeError
          ? "Network or CORS error. Check backend URL or server status."
          : err.message || "Unexpected error occurred."
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#1a4d42] flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(67,179,126,0.15),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.1),_transparent_35%)]" />
      <div className="relative w-full max-w-md">
        <div className="rounded-[2rem] border border-white/10 bg-[#173a32] p-8 shadow-2xl shadow-black/20">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-white mb-2">Login</h1>
            <p className="text-slate-300 text-sm">Sign in to your account to continue</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/5 text-white placeholder-slate-400 focus:outline-none focus:border-emerald-400 focus:bg-white/10 transition"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/5 text-white placeholder-slate-400 focus:outline-none focus:border-emerald-400 focus:bg-white/10 transition"
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="w-full py-3 px-4 rounded-lg bg-[#8ff0b3] text-[#0f2b24] font-bold text-base hover:bg-[#b3f7ca] transition shadow-lg shadow-black/20"
            >
              Sign in
            </button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-sm font-semibold text-slate-400">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <a
            href={`${BACKEND_URL}/api/auth/google/login`}
            className="mt-6 flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg border border-white/20 bg-white/5 text-white font-semibold hover:bg-white/10 transition"
            aria-label="Sign in with Google"
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </a>

          {forgotPasswordMessage && (
            <p className="mt-4 p-3 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-200 text-sm font-medium text-center">
              {forgotPasswordMessage}
            </p>
          )}

          {error && (
            <p className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm font-medium text-center">
              {error}
            </p>
          )}

          <p className="mt-6 text-center text-sm text-slate-300">
            Don't have an account?{" "}
            <Link to="/signup" className="font-semibold text-emerald-300 hover:text-emerald-200 transition">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
